import React from 'react';
import styled from 'styled-components';
import { FiArrowRight } from 'react-icons/fi';
import { colors, typography } from '../../theme/colors';
import { formatAmount } from '../../utils/documentNumbering';

// Sticky pipeline summary at the top of the cycle report. Each pill is one
// lane; the trailing pill is "Outstanding" (computed from invoices).
//
// Numbers are intentionally additive across visible chains. Filters cascade
// here too — the strip always reflects the current canvas view.
const CycleKPIStrip = ({ stats, lanes, currencyCode }) => {
  if (!stats || !lanes) return null;
  const sym = currencyCode || 'PKR';
  return (
    <Strip>
      {lanes.map((lane, i) => {
        const bucket = stats.byLane[lane.id] || { count: 0, total: 0 };
        return (
          <React.Fragment key={lane.id}>
            <Pill>
              <Label>{lane.label}</Label>
              <Total>{sym} {formatAmount(bucket.total)}</Total>
              <Count>{bucket.count} doc{bucket.count === 1 ? '' : 's'}</Count>
            </Pill>
            {i < lanes.length - 1 && <Arrow><FiArrowRight size={12} /></Arrow>}
          </React.Fragment>
        );
      })}
      <Arrow><FiArrowRight size={12} /></Arrow>
      <Pill $alert={stats.outstanding > 0}>
        <Label>Outstanding</Label>
        <Total>{sym} {formatAmount(stats.outstanding || 0)}</Total>
        <Count>
          {stats.totalInvoiced > 0
            ? `${Math.round(((stats.totalPaid / stats.totalInvoiced) * 100))}% cleared`
            : 'n/a'}
        </Count>
      </Pill>
    </Strip>
  );
};

export default CycleKPIStrip;

const Strip = styled.div`
  display: flex;
  align-items: stretch;
  gap: 4px;
  padding: 10px 12px;
  background: white;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 6px;
  overflow-x: auto;
`;

const Pill = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 14px;
  background: ${p => p.$alert ? `${colors.error.main}10` : colors.background?.paper || '#fafafa'};
  border: 1px solid ${p => p.$alert ? colors.error.main : colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 6px;
  min-width: 130px;
`;
const Label = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${colors.text.secondary};
`;
const Total = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.text.primary};
  font-family: ${typography.fontFamily.mono};
`;
const Count = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.text.secondary};
`;
const Arrow = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${colors.text.secondary};
`;
